import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Clock, MapPin, ArrowRight } from 'lucide-react';
import { trackServiceView, trackServiceClick } from '../utils/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ServiceFinder = ({ onClearCountry }) => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [services, setServices] = useState([]);
  const [countryServiceCounts, setCountryServiceCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [sectionContent, setSectionContent] = useState({
    title: 'Our Visa Services',
    subtitle: 'Choose from our comprehensive range of visa consultation services'
  });
  const [backgroundSettings, setBackgroundSettings] = useState({
    services_image: '',
    services_opacity: 0.5
  });
  const observedServices = useRef(new Set()); // Track which services have been viewed

  useEffect(() => {
    fetchServices();
    fetchSectionContent();
    fetchBackgroundSettings();
    fetchCountryServiceCounts();
  }, []);

  // Setup IntersectionObserver for tracking service views
  useEffect(() => {
    if (services.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
            const serviceId = entry.target.dataset.serviceId;
            const serviceName = entry.target.dataset.serviceName;
            
            // Track only once per session
            if (serviceId && !observedServices.current.has(serviceId)) {
              observedServices.current.add(serviceId);
              trackServiceView(serviceId, serviceName);
            }
          }
        });
      },
      { threshold: 0.5 } // Trigger when 50% visible
    );

    // Observe all service cards
    const serviceCards = document.querySelectorAll('[data-service-id]');
    serviceCards.forEach((card) => observer.observe(card));

    return () => {
      serviceCards.forEach((card) => observer.unobserve(card));
    };
  }, [services, selectedCategory]);

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/services`);
      setServices(response.data);
    } catch (error) {
      console.error('Failed to fetch services:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSectionContent = async () => {
    try {
      const response = await axios.get(`${API}/page-content/home`);
      const servicesData = response.data.find(item => item.section_name === 'services');
      if (servicesData && servicesData.content) {
        setSectionContent(servicesData.content);
      }
    } catch (error) {
      console.error('Failed to fetch section content:', error);
    }
  };

  const fetchCountryServiceCounts = async () => {
    try {
      const response = await axios.get(`${API}/country-services`);
      const counts = {};
      response.data.forEach(cs => {
        counts[cs.service_id] = (counts[cs.service_id] || 0) + 1;
      });
      setCountryServiceCounts(counts);
    } catch (error) {
      console.error('Failed to fetch country service counts:', error);
    }
  };

  const fetchBackgroundSettings = async () => {
    try {
      const response = await axios.get(`${API}/page-content/home`);
      let bgData = response.data.find(item => item.section_name === 'backgrounds_v2');
      if (!bgData) {
        bgData = response.data.find(item => item.section_name === 'backgrounds');
      }
      if (bgData && bgData.content) {
        setBackgroundSettings({
          services_image: bgData.content.services_image || '',
          services_opacity: bgData.content.services_opacity || 0.5
        });
      }
    } catch (error) {
      console.error('Failed to fetch background settings:', error);
    }
  };

  const fetchCountryServices = async (serviceId) => {
    try {
      const response = await axios.get(`${API}/country-services/by-service/${serviceId}`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch country services:', error);
      return [];
    }
  };

  const handleServiceClick = (service) => {
    // Track service click
    trackServiceClick(service.id, service.title);
    
    const hasCountryServices = countryServiceCounts[service.id] > 0;
    if (hasCountryServices) {
      // Scroll to Get Visa section
      const element = document.getElementById('get-visa');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      scrollToContact();
    }
  };

  const getButtonText = (service) => {
    const hasCountryServices = countryServiceCounts[service.id] > 0;
    if (hasCountryServices) {
      return 'Explore More';
    }
    return service.button_text || 'Apply Now';
  };

  const scrollToContact = () => {
    // Clear any previously selected country from Top Destinations
    if (onClearCountry) {
      onClearCountry();
    }
    
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      // Focus Full Name input field after scrolling
      setTimeout(() => {
        const nameInput = document.getElementById('name');
        if (nameInput) {
          nameInput.focus();
        }
      }, 600);
    }
  };

  const categories = [
    { id: 'all', label: 'All Services' },
    { id: 'tourist', label: 'Tourist' },
    { id: 'study', label: 'Study' },
    { id: 'immigration', label: 'Immigration' },
    { id: 'family', label: 'Family' },
    { id: 'usa', label: 'USA Visas' },
    { id: 'support', label: 'Support' }
  ];

  const filteredServices = selectedCategory === 'all' 
    ? services 
    : services.filter(service => service.category === selectedCategory);

  if (loading) {
    return (
      <section id="services" className="services-section">
        <div className="container">
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Loading services...</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <>
      <section id="services" className="services-section" style={{
        backgroundImage: backgroundSettings.services_image ? `url(${backgroundSettings.services_image})` : 'none',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        position: 'relative'
      }}>
        {backgroundSettings.services_image && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: `rgba(255, 249, 242, ${backgroundSettings.services_opacity})`,
            pointerEvents: 'none'
          }} />
        )}
        <div className="container" style={{ position: 'relative', zIndex: 1 }}>
          <div className="section-header">
            <h2 className="heading-1">{sectionContent.title}</h2>
            <p className="body-large section-subtitle">
              {sectionContent.subtitle}
            </p>
          </div>

          <div className="category-filters">
            {categories.map(category => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`btn-tag ${selectedCategory === category.id ? 'active' : ''}`}
              >
                {category.label}
              </button>
            ))}
          </div>

          <div className="voice-grid">
            {filteredServices.map(service => (
              <div 
                key={service.id} 
                className={`voice-card ${service.accent_color}`}
                data-service-id={service.id}
                data-service-name={service.title}
              >
                <h3 className="voice-card-title">{service.title}</h3>
                <p className="voice-card-description">{service.description}</p>
                
                <div className="service-meta">
                  <div className="meta-item">
                    <Clock size={16} />
                    <span className="caption">{service.duration}</span>
                  </div>
                  <div className="meta-item">
                    <MapPin size={16} />
                    <span className="caption">{service.countries.length} Countries</span>
                  </div>
                </div>

                <button onClick={() => handleServiceClick(service)} className="service-cta">
                  <span className="button-text">{getButtonText(service)}</span>
                  <ArrowRight size={16} />
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default ServiceFinder;